import { useTranslation } from 'react-i18next';
import { InfiosChip, InfiosContainer, InfiosIcon, InfiosLink } from '@infios/uikit/react';
import type { ConfidenceLevel, RecommendationOption } from '../../interfaces/chat';
import { useReferenceAction } from '../../context/ReferenceActionContext';
import { FormattedDateTime } from '../FormattedDateTime';
import styles from './RecommendationCard.module.css';

interface RecommendationCardProps {
  option: RecommendationOption;
}

const CONFIDENCE_COLOR: Record<ConfidenceLevel, { backgroundColor: string; textColor: string }> = {
  HIGH: { backgroundColor: 'var(--chat-success-subtle)', textColor: 'var(--chat-success)' },
  MEDIUM: { backgroundColor: 'var(--chat-warning-subtle)', textColor: 'var(--chat-warning)' },
  LOW: { backgroundColor: 'var(--chat-error-subtle)', textColor: 'var(--chat-error)' },
};

/**
 * One resolution option. Invariant #2: the confidence badge always renders
 * with `confidenceRationale` alongside it — `confidenceRationale` is a
 * required field, so this is enforced by construction, not a conditional.
 * Invariant #4: `humanActionInstruction` is required and always rendered.
 */
export function RecommendationCard({ option }: RecommendationCardProps) {
  const { t } = useTranslation();
  const onReference = useReferenceAction();

  return (
    <InfiosContainer type="card" className={`${styles.card} ${option.recommended ? styles.recommended : ''}`}>
      <div className={styles.headRow}>
        <span className={styles.optionLabel}>{option.optionLabel}</span>
        <span className={styles.title}>{option.title}</span>
        {option.recommended ? (
          <InfiosChip size="compact" backgroundColor="var(--chat-accent-subtle)" textColor="var(--chat-accent)">
            {t('ChatbotUI.OrderInsight.Recommended')}
          </InfiosChip>
        ) : null}
      </div>

      <p className={styles.description}>{option.description}</p>

      <div className={styles.confidenceRow}>
        <InfiosChip size="compact" {...CONFIDENCE_COLOR[option.confidence]}>
          {t(`ChatbotUI.OrderInsight.Confidence_${option.confidence}`)}
        </InfiosChip>
        <span className={styles.rationale}>{option.confidenceRationale}</span>
      </div>

      <dl className={styles.etaGrid}>
        <div>
          <dt>{t('ChatbotUI.OrderInsight.EtaOriginal')}</dt>
          <dd>
            <FormattedDateTime iso={option.eta.original} />
          </dd>
        </div>
        <div>
          <dt>{t('ChatbotUI.OrderInsight.EtaRevised')}</dt>
          <dd>
            <FormattedDateTime iso={option.eta.revised} />
          </dd>
        </div>
        {option.costDelta ? (
          <div>
            <dt>{t('ChatbotUI.OrderInsight.CostDelta')}</dt>
            <dd>{option.costDelta}</dd>
          </div>
        ) : null}
      </dl>

      <div className={styles.actionRow}>
        <InfiosIcon name="hammer-wrench" className={styles.actionIcon} />
        <div>
          <span className={styles.actionLabel}>{t('ChatbotUI.OrderInsight.NextStep')}</span>
          <p className={styles.actionText}>{option.humanActionInstruction}</p>
        </div>
      </div>

      {option.reference ? (
        <InfiosLink
          className={styles.refLink}
          showIcon
          onClick={() => onReference?.(option.reference!.reference)}
        >
          {option.reference.label || t('ChatbotUI.OrderInsight.MoreOnOption', { label: option.optionLabel })}
        </InfiosLink>
      ) : null}
    </InfiosContainer>
  );
}
